/**
 * SelfReferralForm Component
 *
 * A comprehensive form component for collecting patient information during
 * the self-referral process. Features real-time validation, health condition
 * selection, and accessibility support.
 *
 * Features:
 * - Controlled form inputs with validation
 * - Health condition selection with common/other categorization
 * - Real-time validation feedback
 * - Loading states and error handling
 * - Accessibility attributes and keyboard navigation
 *
 * @module components/self-referral/SelfReferralForm
 */

import React, { useState, useCallback, useEffect } from 'react';
import { Button } from '@/components/ui/Button';
import { FormField } from '@/components/ui/FormField';
import { Input } from '@/components/ui/Input';
import { TextArea } from '@/components/ui/TextArea';
import { CountryPhoneSelector } from '@/components/ui/CountryPhoneSelector';
import { useTheme } from '@/hooks/useTheme';
import { cn } from '@/utils/cn';
import { validateSelfReferralForm } from '@/utils/validation/referralValidation';
import { HealthConditionsSelector } from '../ui';
import type {
  SelfReferralFormProps,
  SelfReferralFormData,
  ValidationErrors
} from './types';
import { logger } from '@/services';

/**
 * SelfReferralForm Component
 */
export const SelfReferralForm: React.FC<SelfReferralFormProps> = ({
  onSubmit,
  isLoading,
  error,
  initialData,
  submitLabel = 'Find My Dietitian'
}) => {
  const { isDark } = useTheme();
  // Form state
  const [formData, setFormData] = useState<SelfReferralFormData>({
    first_name: initialData?.first_name || '',
    last_name: initialData?.last_name || '',
    email: initialData?.email || '',
    phone: initialData?.phone || undefined,
    date_of_birth: initialData?.date_of_birth || '',
    conditions: initialData?.conditions || [],
    additional_notes: initialData?.additional_notes || ''
  });

  // Validation state
  const [validationErrors, setValidationErrors] = useState<ValidationErrors>({});
  const [hasAttemptedSubmit, setHasAttemptedSubmit] = useState(false);

  // UI state - removed showOtherConditions as it's handled by the MultiSelectInput component

  /**
   * Update form field value
   */
  const updateField = useCallback((field: keyof SelfReferralFormData, value: string | string[] | { countryCode: string; isoCode: string; number: string }) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    // Clear validation error for this field
    if (validationErrors[field]) {
      setValidationErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  }, [validationErrors]);

  /**
   * Handle health condition selection
   */
  const handleConditionsChange = useCallback((conditions: string[]) => {
    updateField('conditions', conditions);
  }, [updateField]);

  /**
   * Handle phone number change
   */
  const handlePhoneChange = useCallback((phoneData: { countryCode: string; isoCode: string; number: string }) => {
    updateField('phone', phoneData);
  }, [updateField]);

  /**
   * Validate form and update errors
   */
  const validateForm = useCallback(() => {
    const validation = validateSelfReferralForm(formData);
    setValidationErrors(validation.errors);
    return validation.isValid;
  }, [formData]);

  /**
   * Handle form submission
   */
  const handleSubmit = useCallback((e: React.FormEvent) => {
    e.preventDefault();

    // Prevent multiple submissions while loading
    if (isLoading) {
      logger.log('Form submission blocked - already loading');
      return;
    }

    setHasAttemptedSubmit(true);

    if (validateForm()) {
      onSubmit(formData);
    }
  }, [formData, validateForm, onSubmit, isLoading]);


  /**
   * Real-time validation on field changes (after first submit attempt)
   */
  useEffect(() => {
    if (hasAttemptedSubmit) {
      validateForm();
    }
  }, [formData, hasAttemptedSubmit, validateForm]);

  /**
   * Get field error message
   */
  const getFieldError = (field: string): string | undefined => {
    return validationErrors[field]?.[0];
  };


  // Get today's date for max date validation
  const today = new Date().toISOString().split('T')[0];

  return (
    <form onSubmit={handleSubmit} className="space-y-6" role="form">
      {/* Form error display */}
      {error && (
        <div className={cn(
          'p-4 rounded-lg border',
          isDark
            ? 'bg-red-900/20 border-red-400 text-red-300'
            : 'bg-red-50 border-red-200 text-red-800'
        )} data-testid="form-error" role="alert">
          <p className="text-sm font-medium">{error}</p>
        </div>
      )}

      {/* Personal Information Section */}
      <div className="space-y-4">
        <h3 className={cn(
          'text-lg font-semibold',
          isDark ? 'text-white' : 'text-gray-900'
        )}>
          Personal Information
        </h3>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <FormField
            label="First Name"
            required
            error={getFieldError('first_name')}
          >
            <Input
              type="text"
              value={formData.first_name}
              onChange={(e) => updateField('first_name', e.target.value)}
              placeholder="Enter your first name"
              disabled={isLoading}
              autoComplete="given-name"
              aria-required="true"
            />
          </FormField>

          <FormField
            label="Last Name"
            required
            error={getFieldError('last_name')}
          >
            <Input
              type="text"
              value={formData.last_name}
              onChange={(e) => updateField('last_name', e.target.value)}
              placeholder="Enter your last name"
              disabled={isLoading}
              autoComplete="family-name"
              aria-required="true"
            />
          </FormField>
        </div>

        <FormField
          label="Email Address"
          required
          error={getFieldError('email')}
        >
          <Input
            type="email"
            value={formData.email}
            onChange={(e) => updateField('email', e.target.value)}
            placeholder="Enter your email address"
            disabled={isLoading}
            autoComplete="email"
            aria-required="true"
          />
        </FormField>

        <FormField
          label="Phone Number"
          error={getFieldError('phone')}
          helperText="Optional: Your phone number for important notifications"
        >
          <CountryPhoneSelector
            value={formData.phone ? {
              countryCode: `+${formData.phone.countryCode}`,
              isoCode: formData.phone.isoCode,
              number: formData.phone.number
            } : {
              countryCode: '+61',
              isoCode: 'AU',
              number: ''
            }}
            onChange={handlePhoneChange}
            disabled={isLoading}
            placeholder="Enter phone number"
          />
        </FormField>

        <FormField
          label="Date of Birth"
          error={getFieldError('date_of_birth')}
          helperText="Optional: Helps us match you with age-appropriate nutrition guidance. Must be at least 13 years old."
        >
          <Input
            type="date"
            value={formData.date_of_birth}
            onChange={(e) => updateField('date_of_birth', e.target.value)}
            max={today}
            disabled={isLoading}
            autoComplete="bday"
          />
        </FormField>

        <FormField
          label="Health Conditions or Goals"
          required
          error={getFieldError('conditions')}
        >
          <HealthConditionsSelector
            selectedConditions={formData.conditions}
            onConditionsChange={handleConditionsChange}
            error={getFieldError('conditions')}
            disabled={isLoading}
          />
        </FormField>

        <FormField
          label="Additional Notes"
          error={getFieldError('additional_notes')}
          helperText="Optional: Tell us anything else that might help us match you with the right dietitian"
        >
          <TextArea
            value={formData.additional_notes || ''}
            onChange={(e) => updateField('additional_notes', e.target.value)}
            placeholder="Tell us about your goals, preferences, or any other relevant information..."
            rows={4}
            disabled={isLoading}
            maxLength={1000}
          />
        </FormField>
      </div>

      {/* Submit Button */}
      <div className={`flex justify-center pt-6 border-t ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
        <Button
          type="submit"
          disabled={isLoading}
          className="min-w-[120px]"
        >
          {isLoading ? (
            <div className="flex items-center gap-2">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
              <span>Processing...</span>
            </div>
          ) : (
            submitLabel
          )}
        </Button>
      </div>

      {/* Form Summary + collection notice (design-care-coordinator-referral-tracking §4.6, D10) */}
      <div className={cn(
        'space-y-2 text-sm',
        isDark ? 'text-secondary-400' : 'text-secondary-600'
      )}>
        <p>
          Your information is secure and will only be shared with your selected dietitian.
          By continuing, you confirm that the information provided is accurate.
        </p>
        <p data-testid="collection-notice">
          If you don&apos;t finish, a BiteWise care coordinator may contact you using these details to
          help you complete your referral. See our{' '}
          <a href="/privacy" className="underline">Privacy Policy</a>.
        </p>
      </div>
    </form>
  );
};

export default SelfReferralForm;